import { useEffect, useState } from 'react'
import {
  Card,
  CardContent,
  Typography,
  Box,
  LinearProgress,
  Tooltip,
  IconButton,
} from '@mui/material'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import TrendingDownIcon from '@mui/icons-material/TrendingDown'
import RemoveIcon from '@mui/icons-material/Remove'
import { WidgetProps, KPIMetric } from './types'

interface KPIScorecardWidgetProps extends WidgetProps {
  metric: KPIMetric
  showTarget?: boolean
  showTrend?: boolean
}

const KPIScorecardWidget = ({
  config,
  metric,
  showTarget = true,
  showTrend = true,
  onUpdate,
  onRemove,
}: KPIScorecardWidgetProps) => {
  const [value, setValue] = useState(metric.value)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    setValue(metric.value)
  }, [metric.value])

  const formatValue = (val: number): string => {
    if (metric.format === 'currency') {
      return new Intl.NumberFormat('en-KE', {
        style: 'currency',
        currency: 'KES',
        maximumFractionDigits: 0,
      }).format(val)
    }
    if (metric.format === 'percentage') {
      return `${val.toFixed(1)}%`
    }
    return new Intl.NumberFormat('en-US').format(val)
  }

  const getProgress = (): number => {
    if (!metric.target || metric.target === 0) return 0
    return Math.min((value / metric.target) * 100, 100)
  }

  const getColor = (): 'success' | 'warning' | 'error' | 'info' => {
    const progress = getProgress()
    if (progress >= 90) return 'success'
    if (progress >= 70) return 'warning'
    return 'error'
  }

  const getTrendColor = (): 'success' | 'error' | 'default' => {
    if (!metric.trend) return 'default'
    if (metric.trend.direction === 'up') return 'success'
    if (metric.trend.direction === 'down') return 'error'
    return 'default'
  }

  const progress = getProgress()
  const color = getColor()
  const trendColor = getTrendColor()

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        '&:hover .widget-actions': {
          opacity: 1,
        },
      }}
    >
      <Box
        className="widget-actions"
        sx={{
          position: 'absolute',
          top: 8,
          right: 8,
          opacity: 0,
          transition: 'opacity 0.2s',
          zIndex: 1,
        }}
      >
        {onRemove && (
          <IconButton size="small" onClick={onRemove} sx={{ bgcolor: 'background.paper' }}>
            <RemoveIcon fontSize="small" />
          </IconButton>
        )}
      </Box>

      <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
            {metric.label}
          </Typography>
          {showTrend && metric.trend && (
            <Tooltip title={`${metric.trend.direction === 'up' ? '+' : ''}${metric.trend.percentage.toFixed(1)}%`}>
              <Box>
                {metric.trend.direction === 'up' && <TrendingUpIcon color={trendColor === 'default' ? 'inherit' : trendColor} fontSize="small" />}
                {metric.trend.direction === 'down' && <TrendingDownIcon color={trendColor === 'default' ? 'inherit' : trendColor} fontSize="small" />}
                {metric.trend.direction === 'neutral' && <RemoveIcon color="inherit" fontSize="small" />}
              </Box>
            </Tooltip>
          )}
        </Box>

        <Typography
          variant="h4"
          sx={{
            fontWeight: 600,
            mb: showTarget ? 1 : 0,
            fontSize: { xs: '1.5rem', sm: '2rem' },
          }}
        >
          {formatValue(value)}
        </Typography>

        {showTarget && metric.target && (
          <Box sx={{ mt: 'auto' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
              <Typography variant="caption" color="text.secondary">
                Target: {formatValue(metric.target)}
              </Typography>
              <Typography variant="caption" color="text.secondary" fontWeight="medium">
                {progress.toFixed(0)}%
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={progress}
              color={color}
              sx={{
                height: 8,
                borderRadius: 1,
                bgcolor: 'grey.200',
              }}
            />
          </Box>
        )}

        {metric.previousValue !== undefined && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
            Previous: {formatValue(metric.previousValue)}
          </Typography>
        )}
      </CardContent>
    </Card>
  )
}

export default KPIScorecardWidget

